/**
 * Generator photo markup vocabulary and caps — client mirror of
 * functions-shared/src/types/generatorPhotoMarkup.ts (the client cannot import that package).
 * The editor enforces these caps before saving; the server enforces them again.
 *
 * KEEP THIS FILE IMPORT-FREE: functions/src/fieldService/gallery/__tests__/collections.test.ts
 * loads it from the functions test runner and fails if these values drift from the server copy.
 */
export const MARKUP_COLORS = ['red', 'yellow', 'green', 'blue', 'white', 'black'] as const;
export type MarkupColor = (typeof MARKUP_COLORS)[number];

export const MARKUP_WIDTHS = ['thin', 'medium', 'thick'] as const;
export type MarkupWidth = (typeof MARKUP_WIDTHS)[number];

export const MARKUP_TEXT_SIZES = ['small', 'medium', 'large'] as const;
export type MarkupTextSize = (typeof MARKUP_TEXT_SIZES)[number];

export const MARKUP_SHAPE_TYPES = ['arrow', 'ellipse', 'freehand', 'text', 'textBox'] as const;
export type MarkupShapeType = (typeof MARKUP_SHAPE_TYPES)[number];

export const MARKUP_LIMITS = {
  maxShapes: 100,
  maxPointsPerStroke: 1000,
  maxTotalPoints: 20000,
  maxTextLength: 200,
  maxShapeIdLength: 40,
  revisionsKept: 20,
} as const;

export const MARKUP_REVISIONS_SUBCOLLECTION = 'markupRevisions';

/** A point in NORMALISED (0..1) coordinates of the upright, un-rotated image. */
export interface MarkupPoint {
  x: number;
  y: number;
}

interface MarkupShapeBase {
  id: string;
  color: MarkupColor;
  width: MarkupWidth;
}

export interface MarkupArrow extends MarkupShapeBase {
  type: 'arrow';
  from: MarkupPoint;
  to: MarkupPoint;
}

export interface MarkupEllipse extends MarkupShapeBase {
  type: 'ellipse';
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

export interface MarkupFreehand extends MarkupShapeBase {
  type: 'freehand';
  points: MarkupPoint[];
}

export interface MarkupText extends MarkupShapeBase {
  type: 'text';
  at: MarkupPoint;
  text: string;
  size: MarkupTextSize;
}

export interface MarkupTextBox extends MarkupShapeBase {
  type: 'textBox';
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
  size: MarkupTextSize;
}

export type MarkupShape = MarkupArrow | MarkupEllipse | MarkupFreehand | MarkupText | MarkupTextBox;
